import React, { FC, useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import Clear from '@crehana/crehana-icons/lib/Clear';
import cn from 'classnames';
import IconButton from '../Buttons/IconButton';
import { SnackbarProps, SnackbarPosition } from './types';
import { baseLighter } from '../styles/colors';
import { stateIcons, stateIconColors } from '../styles/states';
import Text from '../Text';

const Snack = styled.div<{ position: SnackbarPosition }>`
  max-width: 468px;
  top: 0;
  left: 16px;
  right: 16px;
  &.snack-is-visible {
    visibility: visible;
    opacity: 1;
    transform: translateY(16px);
    transition: opacity 150ms linear, transform 150ms linear;
  }
  &.snack-is-hidden {
    visibility: hidden;
    opacity: 0;
    transform: translateY(0);
    transition: visibility 0s 150ms, opacity 150ms linear,
      transform 150ms linear;
  }
  ${({ theme }) => theme.breakpoints.up('sm')} {
    ${({ position }) => {
      if (position === 'top-center') {
        return `
          right: unset;
          left: 50%;
          &.snack-is-visible {
            visibility: visible;
            opacity: 1;
            transform: translate(-50%, 48px);
            transition: opacity 150ms linear, transform 150ms linear;
          }
          &.snack-is-hidden {
            visibility: hidden;
            opacity: 0;
            transform: translate(-50%, 0);
            transition: visibility 0s 150ms, opacity 150ms linear,
              transform 150ms linear;
          }
        `;
      }
      return `
        ${
          position === 'bottom-right'
            ? 'bottom: 48px; top: unset;'
            : 'top: 48px;'
        }
        left: unset;
        right: 0;
        &.snack-is-visible {
          visibility: visible;
          opacity: 1;
          transform: translateX(-48px);
          transition: opacity 150ms linear, transform 150ms linear;
        }
        &.snack-is-hidden {
          visibility: hidden;
          opacity: 0;
          transform: translateX(0);
          transition: visibility 0s 150ms, opacity 150ms linear,
            transform 150ms linear;
        }
      `;
    }}
  }
`;

const Snackbar: FC<SnackbarProps> = ({
  open,
  position = 'bottom-right',
  message,
  state,
  onDismiss,
  showCloseBtn,
  dismissOnClickOutside,
  dismissOnDelay,
  dark,
  className,
  children,
  datatestid,
  ...other
}) => {
  const [isClient, setIsClient] = React.useState(false);
  const [delayTimeout, setDelayTimeout] = useState<number | undefined>();

  const snackbarRef = useRef<HTMLDivElement>(null);

  const IconComponent = state ? stateIcons[state] : undefined;
  const iconColor = state ? stateIconColors[state] : undefined;

  React.useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    // Handle dismiss timeout if necessary
    if (dismissOnDelay) {
      if (delayTimeout) {
        clearTimeout(delayTimeout);
      }
      if (open) {
        setDelayTimeout(
          setTimeout(() => {
            if (onDismiss) {
              onDismiss();
            }
          }, dismissOnDelay),
        );
      }
    }
  }, [open]);

  const onClose = (e: any) => {
    e.preventDefault();
    if (open && onDismiss) {
      onDismiss();
    }
  };

  useEffect(() => {
    const listener = (
      event:
        | HTMLElementEventMap['mousedown']
        | HTMLElementEventMap['touchstart'],
    ) => {
      if (
        snackbarRef.current &&
        !snackbarRef.current.contains(event.target as Node) &&
        onDismiss
      ) {
        onDismiss();
      }
    };

    if (dismissOnClickOutside) {
      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);
    } else {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    }

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [dismissOnClickOutside, onDismiss]);

  if (isClient) {
    return ReactDOM.createPortal(
      <Snack
        ref={snackbarRef}
        className={cn(
          'fixed z-snackbar shadow-snackbar-mobile sm:shadow-snackbar',
          dark ? 'bg-base-dark text-white' : 'bg-white text-base-main',
          open ? 'snack-is-visible' : 'snack-is-hidden',
          {
            'flex items-center': !className && !!state,
            'pr-64': !className && !!state && showCloseBtn,
          },
          className || 'py-16 px-24',
        )}
        position={position}
        data-testid={datatestid}
        {...other}
      >
        {showCloseBtn && (
          <IconButton
            className="absolute top-0 right-0 mr-8 mt-8 sm:mr-16 sm:mt-16 cursor-pointer"
            xs
            onClick={onClose}
            customIcon={Clear}
            customColor={baseLighter}
            customSize={24}
          />
        )}
        {IconComponent && iconColor && (
          <IconComponent color={iconColor} size={24} className="mr-16" />
        )}
        {message ? (
          <Text type="body4" color={dark ? 'white' : 'base-main'}>
            {message}
          </Text>
        ) : (
          children
        )}
      </Snack>,
      document.body,
    );
  }
  return null;
};

export default Snackbar;
